import React, { FC, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import {
  SearchType,
  TDealerLocatorLandingProps,
  TDealershipResult,
  TDealershipSuggestion,
  TPlaceCoordinates,
  onDealerResultsProps,
  TCountry,
  TUpdateUrlQueryStringParamsProps,
  TMapSettings,
  TResultViewMode,
} from './types/dealerLocatorLanding.types';
import { DealerLocatorLandingModel } from './models/dealerLocatorLanding.model';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import DealerLocatorMap from './components/dealerLocatorMap';
import DealerLocatorSearch from './components/dealerLocatorSearch';
import DealerResultsListing from './components/dealerResultsListing';
import DealerLocatorDesktopFilters from './components/dealerLocatorDesktopFilters';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import DealerLocatorMobileFilters from './components/dealerLocatorMobileFilters';
import FirebaseContextProvider from './components/dealerLocatorFirebaseContextProvider';
import { useNavigate } from 'react-router-dom';
import { windowService } from '../../utils/window.utils.js';
import { DL_QUERY_STRINGS } from '../../constants/dealerLocator.constants';
import useCountry from './hooks/useCountry.effect';
import useRegion from './hooks/useRegion.effect';
import useSelectedFiltersState from './hooks/useSelectedFiltersState.effect';
import { fetchDealersByGeoCode, getInitialMapSettings, getMapSettingsFromCountry } from './dealerLocator.utils';
import useUserLocationState from './hooks/useUserLocationState.effect';
import DictionaryTerm from '../@shared/dictionaryTerm';
import Icon from '../@shared/icon';
import { iconSize } from '../../constants/icon.constants';
import useMediaQuery from '../../hooks/useMediaQuery';
import useSitecoreContext from '../../hooks/useSitecoreContext';
import APIProviderGoogleMap from '../@shared/apiProviderGoogleMap';
import './styles.scss';

const DealerLocatorLanding: FC<TDealerLocatorLandingProps> = (props) => {
  const {sitecoreContext} = useSitecoreContext();
  const model = new DealerLocatorLandingModel(props, sitecoreContext);
  const defaultCountry = model.countries.find((country) => country.IsoCode === model.defaultCountry) ?? null;
  const preselectedCountry = model.hasDefaultCountryPreselected ? defaultCountry : null;
  const defaultForResulsShown = model.searchType === SearchType.NoSearch;
  const [country, setCountry] = useCountry(preselectedCountry, model);
  const [region, setRegion] = useRegion(null, country);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService>();
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>();
  const [dealers, setDealers] = useState<TDealershipSuggestion | TDealershipSuggestion[]>(model.noSearchDealerResults ?? []);
  const [dealerIdForPinWithHover, setDealerIdForPinWithHover] = useState<string | null>(null);
  const [place, setPlace] = useState<TPlaceCoordinates | undefined>();
  const [resultAndFilterShown, setResultAndFilterShown] = useState<boolean>(defaultForResulsShown);
  const [clickedDealerNumber, setClickedDealerNumber] = useState<string | null>(null);
  const [mapSettings, setMapSettings] = useState<TMapSettings>(getInitialMapSettings(country, model));
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-l']})`);
  const [isLoadingDealers, setIsLoadingDealers] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useSelectedFiltersState({
    equipmentSelectedOptions: {},
    serviceSelectedOptions: {},
  });
  const [dealerIdPinHovered, setDealerIdPinHovered] = useState<boolean>(false);

  const navigate = useNavigate();
  const [userLocation, valueIsFromQuery, setUserLocation] = useUserLocationState();
  const controllerRef = useRef<AbortController | null>();
  const countryIsoCodeWithFallback =
    model.searchType === SearchType.SearchByLocationOrDealer ? model.defaultCountry : country?.IsoCode || model.defaultCountry;

  const [filteredDealerResults, setFilteredDealerResults] = useState({
    dealerLocatorEquipmentsResults: '',
    dealerLocatorServicesResults: '',
  });

  const [resultsViewModeInTablet, setResultsViewModeInTablet] = useState<TResultViewMode>(model.defaultViewMode);

  const updateFilters = useCallback((newFilters = {}) => {
    setSelectedFilters({ ...selectedFilters, ...newFilters });
  }, []);

  useEffect(() => {
    const updatedFilters = { ...selectedFilters };
    const equipmentsResults = Object.values(updatedFilters?.equipmentSelectedOptions).flat();
    const servicesResults = Object.values(updatedFilters?.serviceSelectedOptions).flat();
    const dealerLocatorEquipmentsResults = equipmentsResults
      ?.map((infoIndex) => {
        return model?.filters?.Equipments?.[infoIndex]?.Title;
      })
      .join();
    const dealerLocatorServicesResults = servicesResults
      ?.map((infoIndex) => {
        return model?.filters?.Services?.[infoIndex]?.Title;
      })
      .join();

    setFilteredDealerResults({
      dealerLocatorEquipmentsResults,
      dealerLocatorServicesResults,
    });
  }, [JSON.stringify(selectedFilters)]);

  const sortDealersByDistance = (dealers: TDealershipSuggestion[]): TDealershipSuggestion[] => {
    return dealers.sort((a, b) => Number.parseFloat(a?.distance ?? '') - Number.parseFloat(b?.distance ?? ''));
  };

  const getVisibleDealers = (dealers: TDealershipSuggestion[]): TDealershipSuggestion[] => {
    const sortedDealers = sortDealersByDistance(dealers);

    if (model.maxResultsCount) {
      return sortedDealers.slice(0, model.maxResultsCount);
    }

    return sortedDealers;
  };

  const filterDealer = (eachDealer: TDealershipResult, selectedFilterCodes: string[] = []): boolean => {
    const contractDetails = eachDealer?.dealership?.dealershipAttributes?.contractDetails;
    if (selectedFilterCodes.length === 0) return true;

    if (contractDetails?.length) {
      const dealerContractDetailsCodesObj = {};
      contractDetails.forEach((eachContract) => (dealerContractDetailsCodesObj[eachContract.shortDescription] = 1));
      const shouldHideDealer = selectedFilterCodes.find((eachCode) => !dealerContractDetailsCodesObj[eachCode]);
      return !shouldHideDealer;
    }
    return false;
  };

  const updateUrlQueryStringParams = ({
    country,
    region,
    queryText,
    userLocation,
    selectedDealerId,
    placeId,
    updateUrl,
  }: TUpdateUrlQueryStringParamsProps): void => {
    const getQuery = () => new URLSearchParams(windowService().location?.search);
    const queries = getQuery();

    const updateUrlParams = (key, newVal: string) => {
      if (newVal.trim() !== '') {
        queries.set(key, newVal);
      } else {
        queries.delete(key);
      }
    };

    if (!updateUrl) {
      return;
    }

    updateUrlParams(DL_QUERY_STRINGS.COUNTRY, country?.IsoCode ?? '');
    updateUrlParams(DL_QUERY_STRINGS.REGION, region?.gmiCode ?? '');
    updateUrlParams(DL_QUERY_STRINGS.USER_LATITUDE, userLocation?.lat?.toString() ?? '');
    updateUrlParams(DL_QUERY_STRINGS.USER_LONGITUDE, userLocation?.lng?.toString() ?? '');
    updateUrlParams(DL_QUERY_STRINGS.SEARCH_QUERY, queryText ?? '');
    updateUrlParams(DL_QUERY_STRINGS.SELECTED_DEALER, selectedDealerId ?? '');
    updateUrlParams(DL_QUERY_STRINGS.PLACE_ID, placeId ?? '');

    navigate(`?${queries.toString()}`);
  };

  const onDealerResults = ({
    results,
    place,
    queryText,
    userLocation,
    selectedDealerId,
    placeId,
    updateUrl,
  }: onDealerResultsProps): void => {
    setIsLoadingDealers(false);
    setDealers(results);
    setPlace(place);
    setResultAndFilterShown(true);
    updateUrlQueryStringParams({ country, region, queryText, userLocation, selectedDealerId, placeId, updateUrl });
  };

  const filteredDealers = useMemo(() => {
    const selectedFilterCodes = Object.keys({ ...selectedFilters.equipmentSelectedOptions, ...selectedFilters.serviceSelectedOptions });
    if (Array.isArray(dealers)) {
      return getVisibleDealers(dealers.filter((eachDealer) => filterDealer(eachDealer, selectedFilterCodes)));
    } else {
      const canShowDealer = !!dealers?.dealership && filterDealer(dealers, selectedFilterCodes);
      return canShowDealer ? dealers : [];
    }
  }, [dealers, JSON.stringify(selectedFilters)]);

  useEffect(() => {
    if (!userLocation?.lat || !userLocation?.lng || !countryIsoCodeWithFallback) {
      return;
    }

    const apiCallParams = {
      lat: userLocation?.lat,
      lng: userLocation?.lng,
      brand: model.brand,
      pageId: model.pageId,
      language: model.siteLanguage,
      countryIsoCode: countryIsoCodeWithFallback,
    };

    const place = {
      lat: Number(userLocation.lat),
      lng: Number(userLocation.lng),
    };

    setIsLoadingDealers(true);
    fetchDealersByGeoCode(apiCallParams, controllerRef)
      .then((data) => {
        const dealershipResults = data?.dealershipResults || [];

        onDealerResults({
          results: dealershipResults,
          place: place,
          userLocation: place,
          updateUrl: !valueIsFromQuery,
        });
      })
      .catch((error) => {
        console.error('Error: Fetch dealers by Geo Code failed.', error);
        onDealerResults({
          results: [],
          userLocation: place,
          updateUrl: !valueIsFromQuery,
        });
      });
  }, [userLocation, countryIsoCodeWithFallback]);

  const updateMapSettings = (country: TCountry): void => {
    const hasSupportedSearchType =
      model.searchType === SearchType.SearchByCountryAndRegion || model.searchType === SearchType.SearchByCountryAndLocationOrDealer;

    if (!model.hasInteractiveMap && hasSupportedSearchType && country) {
      setMapSettings(getMapSettingsFromCountry(country, model));
    }
  };

  const updateCountry = (country: TCountry) => {
    updateUrlQueryStringParams({ updateUrl: true });
    setCountry(country);
    setDealers([]);
    setResultAndFilterShown(defaultForResulsShown);
    setPlace(undefined);
    updateMapSettings(country);
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }
  const setDealerIdPinFocused = (hovered) => {
    setDealerIdPinHovered(hovered);
  };
  return (
    <FirebaseContextProvider model={model}>
      <div className="dealer-locator-landing">
        <div className={`dealer-locator-landing__heading 
          ${!(resultAndFilterShown && model.areFiltersNotEmpty) ? 'dealer-locator-landing__heading--padding' : ''}`}>
          <div className="dealer-locator-landing__heading-text">
            <Text tag="h1" field={model.title || {}} className="dealer-locator-landing__title" />
            <Text tag="div" field={model.description || {}} className="dealer-locator-landing__description" />
          </div>
          {autocompleteService && placesService && (
            <DealerLocatorSearch
              model={model}
              onDealerResults={onDealerResults}
              country={country}
              setCountry={updateCountry}
              region={region}
              setRegion={setRegion}
              autocompleteService={autocompleteService}
              placesService={placesService}
              setUserLocation={setUserLocation}
              setIsLoadingDealers={setIsLoadingDealers}
              filteredDealerResults={filteredDealerResults}
              sitecoreContext={sitecoreContext}
            />
          )}
          <div className="dealer-locator-landing__tab-wrapper">
            <button
              className={'dealer-locator-landing__tab'}
              onClick={() => setResultsViewModeInTablet(() => (resultsViewModeInTablet === 'list' ? 'map' : 'list'))}
            >
              <DictionaryTerm keyword={resultsViewModeInTablet === 'map' ? 'cnhi-dealerLandingListView' : 'cnhi-dealerLocatorMapView'} />
              <Icon
                iconName={resultsViewModeInTablet === 'map' ? 'set-nav-services-nav-list' : 'geolocation-pin'}
                color="primary"
                size={iconSize.dsk}
                extraClassName="dealer-locator-landing__tab-icon"
              />
            </button>
            {/* TODO: Uncomment below when AllDealers functionality is implemented */}
            {/* <button className={'dealer-locator-landing__tab'}>
              <DictionaryTerm keyword='cnhi-dealerLadingAllDealers' />
              <Icon iconName='arrow-simple-right' color='primary' size={iconSize.dsk} extraClassName='dealer-locator-landing__tab-icon' />
            </button> */}
          </div>
        </div>
        {resultAndFilterShown &&
            model.areFiltersNotEmpty &&
            (isWide ? (
              <DealerLocatorDesktopFilters filters={model.filters} selectedFilters={selectedFilters} updateFilters={updateFilters} />
            ) : (
              <DealerLocatorMobileFilters filters={model.filters} selectedFilters={selectedFilters} updateFilters={updateFilters} />
            ))}
        <div className="dealer-locator-landing__results-and-map-wrapper">
          {resultAndFilterShown && (
            <DealerResultsListing
              model={model}
              dealers={filteredDealers}
              dealerIdForPinWithHover={dealerIdForPinWithHover}
              setDealerIdForPinWithHover={setDealerIdForPinWithHover}
              clickedDealerNumber={clickedDealerNumber}
              setClickedDealerNumber={setClickedDealerNumber}
              isLoadingDealers={isLoadingDealers}
              setDealerIdPinHovered={setDealerIdPinFocused}
              country={country}
              region={region}
              resultsViewModeInTablet={resultsViewModeInTablet}
            />
          )}
          <APIProviderGoogleMap mapApiKey={model.googlaMapApiKey} libraries={['places']}>
            <DealerLocatorMap
              model={model}
              onDealerResults={onDealerResults}
              country={country}
              region={region}
              setPlacesService={setPlacesService}
              setAutocompleteService={setAutocompleteService}
              dealers={filteredDealers}
              place={place}
              dealerIdForPinWithHover={dealerIdForPinWithHover}
              setDealerIdForPinWithHover={setDealerIdForPinWithHover}
              setClickedDealerNumber={setClickedDealerNumber}
              setCountry={setCountry}
              setRegion={setRegion}
              setUserLocation={setUserLocation}
              mapSettings={mapSettings}
              sitecoreContext={sitecoreContext}
              dealerIdPinHovered={dealerIdPinHovered}
              resultsViewModeInTablet={resultsViewModeInTablet}
            />
          </APIProviderGoogleMap>
        </div>
      </div>
    </FirebaseContextProvider>
  );
};

export default DealerLocatorLanding;
